import React, { useState, useCallback } from 'react';
import { MapContainer, TileLayer, Polygon, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

if (typeof document !== 'undefined' && !document.getElementById('wr-map-css')) {
  const s = document.createElement('style');
  s.id = 'wr-map-css';
  s.textContent = `
    .wr-map-popup .leaflet-popup-content-wrapper {
      background:#111a2e !important; border:1px solid #1e2d44 !important;
      border-radius:10px !important; color:#fff !important; font-family:'Figtree', sans-serif;
    }
    .wr-map-popup .leaflet-popup-tip { background:#111a2e !important; }
    .wr-map-popup .leaflet-popup-content { margin:12px 16px; min-width:180px; }
    .wr-pulse-dot { animation: wr-pulse 2s infinite; }
    @keyframes wr-pulse {
      0%,100% { opacity:1; }
      50%      { opacity:0.4; }
    }
  `;
  document.head.appendChild(s);
}

const WORLD_BOUNDS = [[-85, -180], [85, 180]];

const RISK_STYLE = {
  high:     { color: '#E11D48', fillColor: '#E11D48', fillOpacity: 0.18, weight: 2 },
  elevated: { color: '#F59E0B', fillColor: '#F59E0B', fillOpacity: 0.15, weight: 2 },
  advisory: { color: '#EAB308', fillColor: '#EAB308', fillOpacity: 0.12, weight: 1, dashArray: '6 4' },
};

const RISK_LABEL = { high: 'HIGH RISK', elevated: 'ELEVATED', advisory: 'ADVISORY' };
const RISK_COLOR = { high: '#E11D48', elevated: '#F59E0B', advisory: '#EAB308' };

const INCIDENT_COLOR = {
  drone_attack:        '#E11D48',
  missile_attack:      '#E11D48',
  piracy:              '#E11D48',
  armed_robbery:       '#E11D48',
  hijack_attempt:      '#E11D48',
  vessel_seizure:      '#E11D48',
  piracy_approach:     '#F59E0B',
  suspicious_approach: '#F59E0B',
  sighting:            '#F59E0B',
  missile_sighting:    '#F59E0B',
  vessel_harassment:   '#F59E0B',
  mine_warning:        '#EAB308',
};

function incidentColor(type) {
  return INCIDENT_COLOR[type] || '#F59E0B';
}

function relTime(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const h    = Math.floor(diff / 3600000);
  if (h < 1)  return 'just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function ZoneLayer({ zone, onZoneClick }) {
  const style = RISK_STYLE[zone.risk_level] || RISK_STYLE.advisory;

  return (
    <Polygon
      positions={zone.polygon}
      pathOptions={style}
      eventHandlers={{
        click: () => onZoneClick(zone),
      }}
    >
      <Tooltip sticky direction="top" opacity={1} className="wr-map-popup">
        <div style={{ minWidth: 160 }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
            {zone.name}
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: RISK_COLOR[zone.risk_level], marginBottom: 4 }}>
            {RISK_LABEL[zone.risk_level]}
          </div>
          <div style={{ fontSize: 11, color: '#8892a5', lineHeight: 1.4 }}>
            {zone.description}
          </div>
        </div>
      </Tooltip>
    </Polygon>
  );
}

function IncidentMarker({ incident }) {
  const color = incidentColor(incident.type);
  return (
    <CircleMarker
      center={[incident.position.lat, incident.position.lng]}
      radius={5}
      pathOptions={{ color, fillColor: color, fillOpacity: 0.9, weight: 1.5 }}
      className="wr-pulse-dot"
    >
      <Popup className="wr-map-popup" maxWidth={280}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color, fontWeight: 700, marginBottom: 4, textTransform: 'uppercase' }}>
            {incident.type.replace(/_/g, ' ')} · {relTime(incident.date)}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 4 }}>
            {incident.headline}
          </div>
          <div style={{ fontSize: 11, color: '#8892a5' }}>
            {incident.zone} · {incident.vessel_type}
          </div>
        </div>
      </Popup>
    </CircleMarker>
  );
}

export default function WarRiskMap({ zones, incidents, onZoneClick }) {
  const cutoff7d  = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recent    = (incidents || []).filter(
    i => i.position && new Date(i.date).getTime() > cutoff7d
  );

  // Sort: high risk zones on top (rendered last = visually on top)
  const sorted = [...(zones || [])].sort((a, b) => {
    const order = { high: 2, elevated: 1, advisory: 0 };
    return (order[a.risk_level] || 0) - (order[b.risk_level] || 0);
  });

  return (
    <div style={{ background: '#111a2e', borderRadius: 10, border: '1px solid #1e2d44', overflow: 'hidden' }}>
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid #1e2d44',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
      }}>
        <div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 13, fontWeight: 600, color: '#fff' }}>
            War Risk Zone Map
          </div>
          <div style={{ fontSize: 11, color: '#8892a5', marginTop: 2 }}>
            JWC listed areas · {recent.length} UKMTO incidents in last 7 days · click zone for details
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {[
            { color: '#E11D48', label: 'High Risk' },
            { color: '#F59E0B', label: 'Elevated'  },
            { color: '#EAB308', label: 'Advisory'  },
          ].map(({ color, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 12, height: 12, borderRadius: 2, background: color, opacity: 0.7 }} />
              <span style={{ fontSize: 10, color: '#8892a5', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '.5px' }}>
                {label}
              </span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#E11D48' }} />
            <span style={{ fontSize: 10, color: '#8892a5', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '.5px' }}>
              Incident
            </span>
          </div>
        </div>
      </div>

      <MapContainer
        center={[20, 30]}
        zoom={2.5}
        minZoom={2}
        maxZoom={6}
        zoomControl={true}
        scrollWheelZoom={false}
        dragging={true}
        doubleClickZoom={true}
        touchZoom={true}
        boxZoom={false}
        keyboard={true}
        attributionControl={false}
        maxBounds={WORLD_BOUNDS}
        maxBoundsViscosity={0.8}
        worldCopyJump={false}
        style={{ height: 480, width: '100%', background: '#0a1117' }}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
          noWrap={true}
          bounds={WORLD_BOUNDS}
        />

        {sorted.map(zone => (
          <ZoneLayer key={zone.id} zone={zone} onZoneClick={onZoneClick} />
        ))}

        {recent.map(incident => (
          <IncidentMarker key={incident.id} incident={incident} />
        ))}
      </MapContainer>
    </div>
  );
}
